
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Maximize2, Minimize2 } from 'lucide-react';
import Confetti from "./Confetti";
import InteractiveBarChart from "./templates/InteractiveBarChart";
import BarChartExamples from "./templates/BarChartExamples";
import ChartVisualization from "./templates/ChartVisualization";
import SimpleChartBuilder from "./templates/SimpleChartBuilder";
import ChartStepTemplate from "./templates/ChartStepTemplate";
import DownloadableFile from './DownloadableFile';
import ClickableImage from './components/ClickableImage'; // Import the ClickableImage component
import './components/TemplateSelector.css'; // Import the CSS file


// Add these helper functions near the top of your file, after your imports

// Helper function to get font size class
const getFontSize = (fontSize) => {
  if (!fontSize) return ""

  // Handle existing textSize property (backward compatibility)
  if (fontSize.startsWith("text-")) return fontSize

  // Handle new fontSize property
  switch (fontSize) {
    case "sm":
      return "text-sm"
    case "base":
      return "text-base"
    case "lg":
      return "text-lg"
    case "xl":
      return "text-xl"
    case "2xl":
      return "text-2xl"
    default:
      return ""
  }
}

// Helper function to get font weight class
const getFontWeight = (fontWeight) => {
  if (!fontWeight) return ""

  switch (fontWeight) {
    case "normal":
      return "font-normal"
    case "medium":
      return "font-medium"
    case "semibold":
      return "font-semibold"
    case "bold":
      return "font-bold"
    default:
      return ""
  }
}
// Find your existing getThemeClass function and modify it, or add this if it doesn't exist
const getThemeClass = (theme) => {
  if (!theme) return ""

  switch (theme) {
    case "blue":
      return "theme-blue"
    case "green":
      return "theme-green"
    case "red":
      return "theme-red"
    case "yellow":
      return "theme-yellow"
    case "purple":
      return "theme-purple"
    case "dark":
      return "theme-dark"
    case "grey":
      return "theme-grey"
    case "light":
      return "theme-light"
    case "orange":
      return "theme-orange"
    default:
      return ""
  }
}

// Helper function to get text alignment class
const getTextAlignment = (textAlignment) => {
  if (!textAlignment) return ""

  switch (textAlignment) {
    case "left":
      return "text-left"
    case "center":
      return "text-center"
    case "right":
      return "text-right"
    default:
      return ""
  }
}

// Helper function to get background color
const getBackgroundColor = (backgroundColor) => {
  if (!backgroundColor) return null

  // Handle theme variables
  if (backgroundColor.startsWith && backgroundColor.startsWith("var(--")) {
    return { backgroundColor }
  }

  // Handle Tailwind classes
  if (backgroundColor.startsWith && backgroundColor.startsWith("bg-")) {
    return backgroundColor
  }

  // Handle hex colors
  return { backgroundColor }
}

// Helper function to get text color
const getTextColor = (textColor) => {
  if (!textColor) return null

  // Handle theme variables
  if (textColor.startsWith && textColor.startsWith("var(--")) {
    return { color: textColor }
  }

  // Handle Tailwind classes
  if (textColor.startsWith && textColor.startsWith("text-")) {
    return textColor
  }

  // Handle hex colors
  return { color: textColor }
}

// Helper function to get spacing class
const getSpacingClass = (spacing) => {
  if (!spacing) return "gap-4"

  switch (spacing) {
    case "tight":
      return "gap-2"
    case "normal":
      return "gap-4"
    case "loose":
      return "gap-8"
    default:
      return spacing.startsWith && spacing.startsWith("gap-") ? spacing : "gap-4"
  }
}

// Helper function to get alignment class
const getAlignmentClass = (alignment) => {
  if (!alignment) return ""

  switch (alignment) {
    case "left":
      return "items-start"
    case "center":
      return "items-center"
    case "right":
      return "items-end"
    default:
      return alignment.startsWith && alignment.startsWith("items-") ? alignment : ""
  }
}

// Add these style handling functions after your existing helper functions

// Helper function to get background color style
const getBackgroundColorStyle = (backgroundColor) => {
  if (!backgroundColor) return { className: "", style: {} }

  // Handle theme variables
  if (backgroundColor.startsWith && backgroundColor.startsWith("var(--")) {
    return { className: "", style: { backgroundColor } }
  }

  // Handle Tailwind classes
  if (backgroundColor.startsWith && backgroundColor.startsWith("bg-")) {
    return { className: backgroundColor, style: {} }
  }

  // Handle hex colors
  return { className: "", style: { backgroundColor } }
}

// Helper function to get text color style
const getTextColorStyle = (textColor) => {
  if (!textColor) return { className: "", style: {} }

  // Handle theme variables
  if (textColor.startsWith && textColor.startsWith("var(--")) {
    return { className: "", style: { color: textColor } }
  }

  // Handle Tailwind classes
  if (textColor.startsWith && textColor.startsWith("text-")) {
    return { className: textColor, style: {} }
  }

  // Handle hex colors
  return { className: "", style: { color: textColor } }
}

// Base Template for consistent layout
// BaseTemplate component (replace in TemplateSelector.js)
const BaseTemplate = ({ content, children }) => {
  // Helper function to render downloadable file - defined here to use in multiple places
  const renderDownloadableFile = (file) => {
    if (!file) return null;
    
    return (
      <div className="my-4">
        <DownloadableFile
          filePath={file.filePath}
          fileName={file.fileName}
          fileType={file.fileType}
          showPreview={file.showPreview !== false}
          previewUrl={file.previewUrl || file.filePath}
          buttonText={file.buttonText || "Download"}
          description={file.description}
        />
      </div>
    );
  };

  return (
    <div className={`template-base ${content?.fullHeightVideo ? 'template-base-fullheight' : ''}`}>
      {content?.title && (
        <h2 className="template-title">
          {content.title}
        </h2>
      )}
      <div className={`template-content ${content?.fullHeightVideo ? 'template-content-fullheight' : ''}`}>
        {children}
        {content?.downloadableFile && renderDownloadableFile(content.downloadableFile)}
      </div>
    </div>
  );
};
// Template for rendering standard content with optional video
// Inside TemplateSelector.js - updating the ContentTemplate component

const ContentTemplate = ({
  content,
  currentLine,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef(null);

  const toggleFullscreen = () => {
    if (videoContainerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoContainerRef.current.requestFullscreen();
      }
      setIsFullscreen(!isFullscreen);
    }
  };
  
  // Create a single container ref for scrolling the component into view
  const containerRef = useRef(null);
  const renderDownloadableFile = (file) => {
    if (!file) return null;
    
    // Log file paths for debugging
    console.log("Rendering downloadable file:", {
      filePath: file.filePath,
      previewUrl: file.previewUrl
    });
    
    return (
      <div className="my-4">
        <DownloadableFile
          filePath={file.filePath}
          fileName={file.fileName}
          fileType={file.fileType}
          showPreview={file.showPreview !== false}
          previewUrl={file.previewUrl}
          buttonText={file.buttonText || "Download"}
          description={file.description}
        />
      </div>
    );
  };
  // Simple function to render a column-based layout
  // Replace your existing renderColumnLayout function with this one
  const renderColumnLayout = () => {
    if (!content?.columns) return null;
    
    // Get layout settings
    const layout = content.layout || {};
    const layoutType = layout.type || "grid";
    const columns = layout.columns || content.columns.length > 1 ? content.columns.length : 2;
    const spacing = getSpacingClass(layout.spacing);
    const alignment = getAlignmentClass(layout.alignment);
    
    // Determine the grid class based on layout type
    let gridClass = "";
    if (layoutType === "grid") {
      gridClass = `grid grid-cols-1 md:grid-cols-${Math.min(columns, 4)} ${spacing} ${alignment}`;
    } else if (layoutType === "flex") {
      gridClass = `flex flex-wrap ${spacing} ${alignment}`;
    } else {
      gridClass = `space-y-4 ${alignment}`;
    }
    
    return (
      <div className={gridClass}>
        {content.columns.map((column, index) => {
          // Track if any items in this column are highlighted
          const hasHighlightedItem = column.highlight && column.highlightItem !== undefined;
          
          // Get theme class (original styling)
          const themeClass = getThemeClass(column.theme);
          
          // Get new styling properties
          const bgColorResult = getBackgroundColorStyle(column.backgroundColor);
          const textColorResult = getTextColorStyle(column.textColor);
          const fontSize = getFontSize(column.fontSize || column.textSize); // Support both properties
          const fontWeight = getFontWeight(column.fontWeight);
          const textAlignment = getTextAlignment(column.textAlignment);
          const padding = column.padding || "p-4";
          
          // Get specific alignment classes for different elements
          // Make center the default for title and description
          const titleAlignment = getTextAlignment(column.titleAlignment || column.textAlignment || "center");
          const descriptionAlignment = getTextAlignment(column.descriptionAlignment || column.textAlignment || "center");
          const contentAlignment = column.contentAlignment || column.textAlignment || "";
          const listAlignment = getTextAlignment(column.listAlignment || column.textAlignment || "");
          
          // Get specific styling for title
          const titleColor = column.titleColor ? getTextColorStyle(column.titleColor) : textColorResult;
          const titleFontSize = column.titleFontSize ? getFontSize(column.titleFontSize) : fontSize;
          const titleFontWeight = column.titleFontWeight ? getFontWeight(column.titleFontWeight) : "font-medium";
          
          // Get specific styling for description
          const descriptionColor = column.descriptionColor ? getTextColorStyle(column.descriptionColor) : textColorResult;
          const descriptionFontSize = column.descriptionFontSize ? getFontSize(column.descriptionFontSize) : fontSize;
          const descriptionFontWeight = column.descriptionFontWeight ? getFontWeight(column.descriptionFontWeight) : fontWeight;
          
          // Combine styles
          const combinedStyle = {
            ...bgColorResult.style,
            ...textColorResult.style
          };
          
          return (
            <div 
              key={index}
              // Add ref to this column div - using arrow function to avoid problems
              ref={el => {
                if (toolRefs.current.length <= index) {
                  toolRefs.current[index] = { current: el };
                } else {
                  toolRefs.current[index] = { current: el };
                }
              }}
              // Standard styling with new properties
              className={`rounded-xl overflow-hidden ${padding} ${themeClass} ${bgColorResult.className} ${textColorResult.className} ${fontSize} ${fontWeight} ${textAlignment}`}
              style={combinedStyle}
              // Add a data attribute for debugging/targeting
              data-highlighted={column.highlight ? "true" : "false"}
            >
              {/* Display image if provided - UPDATED TO USE CLICKABLE IMAGE */}
              {column.imageSrc && (
                <div className={`flex ${contentAlignment || "justify-center"} mb-4`}>
                  <ClickableImage 
                    src={column.imageSrc || "/placeholder.svg"} 
                    alt={column.imageAlt || 'Column image'} 
                    className="max-w-full rounded-lg shadow-sm" 
                  />
                </div>
              )}
              
              {/* Display icon if provided */}
              {column.icon && (
                <div className={`text-4xl mb-4 ${titleAlignment}`}>
                  {column.icon}
                </div>
              )}
              
              {/* Display title if provided */}
              {column.title && (
                <h3 
                  className={`text-lg mb-2 ${titleAlignment} ${titleFontSize} ${titleFontWeight} ${titleColor.className}`}
                  style={titleColor.style}
                >
                  {column.title}
                </h3>
              )}
              
              {/* Display description if provided */}
              {column.description && (
                <p 
                  className={`mb-4 ${descriptionAlignment} ${descriptionFontSize} ${descriptionFontWeight} ${descriptionColor.className}`}
                  style={descriptionColor.style}
                >
                  {column.description}
                </p>
              )}
  
              {/* Render downloadable file if available */}
              {column.downloadableFile && renderDownloadableFile(column.downloadableFile)}
              
              {/* Display list items if provided */}
              {column.listItems && column.listItems.length > 0 && (
                <div className={listAlignment || "text-left"}>
                  {column.listTitle && (
                    <h4 className="font-medium mb-2">{column.listTitle}</h4>
                  )}
                  <ul className="space-y-2">
                    {column.listItems.map((item, i) => {
                      // Determine if this specific item is highlighted
                      const isHighlighted = hasHighlightedItem && column.highlightItem === i;
                      
                      return (
                        <li 
                          key={i} 
                          // Add ref to this list item
                          ref={el => {
                            // Create the observations array for this column if it doesn't exist
                            if (!observationRefs.current[index]) {
                              observationRefs.current[index] = [];
                            }
                            // Set the ref for this specific observation
                            observationRefs.current[index][i] = { current: el };
                          }}
                          // Apply highlight styling if this specific item is highlighted
                          className={`flex items-start gap-2 ${isHighlighted ? "list-item-highlighted" : ""}`}

                          // Add a data attribute for debugging/targeting
                          data-highlighted={isHighlighted ? "true" : "false"}
                        >
                          <span className={`list-marker ${isHighlighted ? "list-marker-highlighted" : ""}`}>
                          {isHighlighted ? "→" : "•"}
                          </span>
                          <span>{typeof item === "string" ? item : item.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

// Add this new function to your ContentTemplate component
const renderSections = () => {
  if (!content?.sections) return null;
  
  return (
    <div className="space-y-8">
      {content.sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="mb-6">
          {section.title && <h3 className="text-xl font-semibold mb-3">{section.title}</h3>}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.columns?.map((column, columnIndex) => {
              // Get theme class (original styling)
              const themeClass = getThemeClass(column.theme);
              
              // Get new styling properties
              const bgColorResult = getBackgroundColorStyle(column.backgroundColor);
              const textColorResult = getTextColorStyle(column.textColor);
              const fontSize = getFontSize(column.fontSize || column.textSize);
              const fontWeight = getFontWeight(column.fontWeight);
              const textAlignment = getTextAlignment(column.textAlignment);
              const padding = column.padding || "p-4";
              
              // Combine styles
              const combinedStyle = {
                ...bgColorResult.style,
                ...textColorResult.style
              };
              
              return (
                <div
                  key={`section-${sectionIndex}-${columnIndex}`}
                  className={`rounded-xl overflow-hidden ${padding} ${themeClass} ${bgColorResult.className} ${textColorResult.className} ${fontSize} ${fontWeight} ${textAlignment}`}
                  style={combinedStyle}
                >
                  {column.icon && <div className="text-4xl text-center mb-4">{column.icon}</div>}
                  {column.title && <h3 className="font-medium mb-2">{column.title}</h3>}
                  {column.description && <p className="mb-4">{column.description}</p>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

  // Set up the refs for all the tools upfront
  const toolRefs = useRef([]);
  const observationRefs = useRef({});
  const tipRefs = useRef([]);

  

  // Make sure we have enough refs for all elements
  useEffect(() => {
    if (content?.tools) {
      toolRefs.current = Array(content.tools.length).fill().map((_, i) => toolRefs.current[i] || React.createRef());
      
      // Create refs for observations
      const obsRefs = {};
      content.tools.forEach((tool, toolIndex) => {
        if (tool.observations) {
          obsRefs[toolIndex] = Array(tool.observations.length).fill().map((_, i) => 
            observationRefs.current[`${toolIndex}-${i}`] || React.createRef()
          );
        }
      });
      observationRefs.current = obsRefs;
    }
    
    if (content?.tips) {
      tipRefs.current = Array(content.tips.length).fill().map((_, i) => tipRefs.current[i] || React.createRef());
    }
  }, [content]);

  // Helper function to detect if we're on mobile
  const isMobileView = () => {
    return window.innerWidth <= 768 || 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Helper function to find highlighted elements
  const findHighlightedElement = () => {
    // First check for highlighted tools
    if (content?.tools) {
      const highlightedToolIndex = content.tools.findIndex((tool, index) => {
        const isHighlightedBySubtitle = tool.subtitleRange && 
          currentLine >= tool.subtitleRange[0] && 
          currentLine <= tool.subtitleRange[1];
        return isHighlightedBySubtitle || tool.highlight;
      });

      if (highlightedToolIndex !== -1) {
        // Check if there's a highlighted item inside this tool
        const tool = content.tools[highlightedToolIndex];
        if (tool.highlightItem !== undefined && 
            observationRefs.current[highlightedToolIndex] &&
            observationRefs.current[highlightedToolIndex][tool.highlightItem]) {
          return {
            ref: observationRefs.current[highlightedToolIndex][tool.highlightItem],
            isNested: true,
            parentRef: toolRefs.current[highlightedToolIndex]
          };
        }
        return {
          ref: toolRefs.current[highlightedToolIndex],
          isNested: false
        };
      }
    }

    // Then check for highlighted tips
    if (content?.tips) {
      const highlightedTipIndex = content.tips.findIndex(tip => tip.highlight);
      if (highlightedTipIndex !== -1) {
        return {
          ref: tipRefs.current[highlightedTipIndex],
          isNested: false
        };
      }
    }
    
    return null;
  };

  // Handle scrolling to highlighted elements with better visibility
  useEffect(() => {
    if (!content) return;
    
    // Small delay to ensure content is fully rendered
    const scrollDelay = setTimeout(() => {
      const highlightedElement = findHighlightedElement();
      if (!highlightedElement || !highlightedElement.ref || !highlightedElement.ref.current) return;
      
      const elementRef = highlightedElement.ref;
      const rect = elementRef.current.getBoundingClientRect();
      const mobile = isMobileView();
      
      // Calculate bottom safe area (space above avatar)
      const avatarHeight = mobile ? 220 : 150;
      const bottomSafeArea = window.innerHeight - avatarHeight;
      
      // Check if element is already fully visible in the safe viewport area
      const isFullyVisible = 
        rect.top >= 150 && // Top margin for context
        rect.bottom <= bottomSafeArea; // Keep above the avatar
      
      if (!isFullyVisible) {
        // Different scrolling strategy for mobile vs desktop
        if (mobile) {
          // For mobile: position in upper portion of screen to avoid avatar
          const yOffset = Math.min(-220, -(window.innerHeight * 0.3)); // At least 150px from top
          const y = rect.top + window.pageYOffset + yOffset;
          
          window.scrollTo({
            top: Math.max(0, y),
            behavior: 'smooth'
          });
          
          // Add data attribute for debugging
          elementRef.current.setAttribute('data-scrolled-to', 'true');
        } else {
          // Desktop behavior
          const yOffset = -150; // Position with margin at top
          const y = rect.top + window.pageYOffset + yOffset;
          
          window.scrollTo({
            top: Math.max(0, y),
            behavior: 'smooth'
          });
        }
      }
      
      // Handle scrolling to nested elements (after parent is in view)
      if (highlightedElement.isNested && highlightedElement.parentRef && highlightedElement.parentRef.current) {
        // Add a slight delay to ensure parent has been scrolled first
        setTimeout(() => {
          const nestedRect = elementRef.current.getBoundingClientRect();
          
          const isNestedFullyVisible = 
            nestedRect.top >= 150 &&
            nestedRect.bottom <= bottomSafeArea;
            
          if (!isNestedFullyVisible) {
            if (mobile) {
              // For mobile: higher positioning
              const nestedY = nestedRect.top + window.pageYOffset - (window.innerHeight * 0.3);
              
              window.scrollTo({
                top: Math.max(0, nestedY),
                behavior: 'smooth'
              });
            } else {
              const nestedY = nestedRect.top + window.pageYOffset - 150;
              
              window.scrollTo({
                top: Math.max(0, nestedY),
                behavior: 'smooth'
              });
            }
          }
        }, 400);
      }
    }, 200); // Longer delay to ensure content is fully rendered
    
    return () => clearTimeout(scrollDelay);
  }, [content, currentLine]);

  const SVG_COMPONENTS = {
    InteractiveBarChart: lazy(() => import("./templates/InteractiveBarChart")),
    BarChartExamples: lazy(() => import("./templates/BarChartExamples")),
    ChartVisualization: lazy(() => import("./templates/ChartVisualization")),
    SimpleChartBuilder: lazy(() => import("./templates/SimpleChartBuilder")),
  };

  const renderSvgComponent = () => {
    if (!content?.svgComponent) return null;

    const SvgComp = SVG_COMPONENTS[content.svgComponent];
    if (!SvgComp) {
      console.warn(`SVG component "${content.svgComponent}" not found`);
      return null;
    }

    return (
      <div className="mb-6 bg-[#EFEAFC]/30 p-4 rounded-xl">
        <Suspense
          fallback={
            <div className="h-40 flex items-center justify-center">
              Loading visualization...
            </div>
          }
        >
          <SvgComp />
        </Suspense>
      </div>
    );
  };

// This function should replace the existing renderVideo function in TemplateSelector.js
// (inside the ContentTemplate component)

const renderVideo = () => {
  if (!content?.videoUrl) return null;

  // Detect video type
  const isVimeo = content.videoUrl.includes("vimeo.com");
  const isVisla = content.videoUrl.includes("visla.us");
  const isGenericEmbed = content.embedUrl || (!isVimeo && !isVisla && content.videoUrl.includes("embed"));

  // Create appropriate src URL based on video type
  let srcUrl = content.videoUrl;
  
  // For Vimeo, extract ID and format URL
  if (isVimeo) {
    const videoId = content.videoUrl.match(/(?:vimeo\.com\/)(\d+)/)?.[1];
    if (videoId) {
      srcUrl = `https://player.vimeo.com/video/${videoId}?h=ef9c24aa47&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`;
    }
  } 
  // For Visla, use the embed URL if not already an embed URL
  else if (isVisla && !content.videoUrl.includes("/embed/")) {
    // Convert regular Visla URL to embed URL
    srcUrl = content.videoUrl.replace("app.visla.us/clip/", "app.visla.us/embed/");
  }
  // For custom embed URLs, use as provided
  else if (content.embedUrl) {
    srcUrl = content.embedUrl;
  }

  // Determine container class based on video type
  const containerClass = `video-container ${content.fullHeightVideo ? 'video-container-fullheight' : ''} ${isVisla ? 'visla-container' : ''}`;

  return (
    <div
      ref={videoContainerRef}
      className={containerClass}
    >
      <iframe
        src={srcUrl}
        allow={isVisla ? "fullscreen *; autoplay; clipboard-write; encrypted-media; picture-in-picture" : "autoplay; fullscreen; picture-in-picture"}
        className={isVisla ? "visla-iframe" : "video-iframe"}
        title={content.videoTitle || "Embedded video"}
        allowFullScreen={true}
        loading="lazy"
      />
      <button
        onClick={toggleFullscreen}
        className="video-fullscreen-button"
      >
        {isFullscreen ? (
          <Minimize2 className="w-5 h-5 text-white" />
        ) : (
          <Maximize2 className="w-5 h-5 text-white" />
        )}
      </button>
    </div>
  );
};

  const renderTools = () => {
    if (!content?.tools) return null;

    return (
      <div className="grid grid-cols-1 grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-6">
        {content.tools.map((tool, index) => {
          // Determine if this tool should be highlighted
          const isHighlightedBySubtitle = tool.subtitleRange && 
            currentLine >= tool.subtitleRange[0] && 
            currentLine <= tool.subtitleRange[1];
          
          const shouldHighlight = isHighlightedBySubtitle || tool.highlight;

          return (
            <motion.div
              key={index}
              ref={el => toolRefs.current[index] = { current: el }}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: shouldHighlight ? 1.05 : 1,
                boxShadow: shouldHighlight
                  ? "0 0 15px rgba(170, 141, 255, 0.5)"
                  : "none",
              }}
              transition={{
                delay: index * 0.2,
                duration: shouldHighlight ? 0.3 : 0.2,
              }}
              className={`bg-${
                tool.theme === "blue"
                  ? "[#D6DCFF]"
                  : tool.theme === "purple"
                  ? "[#EFEAFC]"
                  : "[#F1FFF9]"
              } rounded-xl p-6 ${shouldHighlight ? "ring-2 ring-[#5F29FF]" : ""}`}
              data-highlighted={shouldHighlight ? "true" : "false"}
            >
              <div className="text-center">
                <motion.div
                  animate={{
                    y: [0, -5, 0],
                    rotate: [0, 5, 0, -5, 0],
                    scale: shouldHighlight ? [1, 1.1, 1] : [1, 1, 1],
                  }}
                  transition={{
                    duration: shouldHighlight ? 2 : 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="mb-4"
                >
                  {tool.image ? (
                    <ClickableImage 
                      src={tool.image || "/placeholder.svg"} 
                      alt={tool.title || "Icon"} 
                      className="h-16 w-16 object-contain mx-auto"
                    />
                  ) : (
                    <div className="text-6xl">{tool.icon}</div>
                  )}
                </motion.div>
                <h3 className="text-xl font-medium tool-title mb-2">{tool.title}</h3>
                <p className="tool-description">{tool.description}</p>
                {tool.examples && (
                  <div className="mt-4 flex justify-center gap-4">
                    {tool.examples.map((example, i) => (
                      <span key={i} className="text-2xl">
                        {example}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {tool.observations && (
                <div className="mt-6 border-t border-[#0D065B]/10 pt-4">
                                    <p className="tool-subtitle mb-2">{tool.subTitle}</p>

                  <ul className="space-y-2 text-left">
                    {tool.observations.map((observation, i) => (
                      <motion.li
                        key={i}
                        ref={el => {
                          if (!observationRefs.current[index]) {
                            observationRefs.current[index] = [];
                          }
                          observationRefs.current[index][i] = { current: el };
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: tool.highlightItem === i ? 1.05 : 1,
                          color: tool.highlightItem === i ? "var(--color-text-light)" : "var(--color-text-primary)",
                        }}
                        transition={{
                          delay: i * 0.1,
                          duration: tool.highlightItem === i ? 0.3 : 0.2,
                        }}
                        className={`flex items-start gap-2 ${tool.highlightItem === i ? "list-item-highlighted" : ""}`}

                        data-highlighted={tool.highlightItem === i ? "true" : "false"}
                      >
                        <span
                          className={
                            tool.highlightItem === i ? "observation-text-primary-large" : "observation-text-primary"
                          }
                        >
                          {tool.highlightItem === i ? "→" : "•"}
                        </span>
                        <span>{observation}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    );
  };

  // Replace your existing renderTips function with this one
const renderTips = () => {
  if (!content?.tips) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="achievement-badge"
      >
      <ul className="space-y-2 text-[var(--color-text-primary)]">
        {content.tips.map((tip, index) => {
          // Get styling properties
          const textColorResult = getTextColorStyle(tip.textColor);
          const fontSize = getFontSize(tip.fontSize);
          const fontWeight = getFontWeight(tip.fontWeight);
          
          return (
            <motion.li
              key={index}
              ref={el => tipRefs.current[index] = { current: el }}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: 1,
                x: 0,
                scale: tip.highlight ? 1.05 : 1,
                backgroundColor: tip.highlight
                  ? "var(--color-accent-dark)"
                  : "transparent",
              }}
              transition={{
                delay: index * 0.1,
                duration: tip.highlight ? 0.3 : 0.2,
              }}
              className={`flex items-center gap-2 p-2 rounded-md ${
                tip.highlight ? "text-white" : textColorResult.className
              } ${fontSize} ${fontWeight}`}
              style={tip.highlight ? {} : textColorResult.style}
            >
              <motion.span
                animate={{
                  rotate: [0, 10, -10, 0],
                  scale: [1, tip.highlight ? 1.3 : 1.2, 1],
                }}
                transition={{
                  duration: tip.highlight ? 1 : 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: index * 0.2,
                }}
              >
                {tip.image ? (
                  <ClickableImage 
                    src={tip.image || "/placeholder.svg"} 
                    alt="Tip icon" 
                    className="h-6 w-6 object-contain"
                  />
                ) : (
                  <span className="text-xl">{tip.icon}</span>
                )}
              </motion.span>
              <span className={tip.highlight ? "font-medium" : ""}>
                {tip.text}
              </span>
            </motion.li>
          )
        })}
      </ul>
    </motion.div>
  );
};

  if (!content?.title && content?.mainContent) {
    content.title = content.mainContent;
  }
  
  // Update your ContentTemplate return statement to include sections rendering
return (
  <BaseTemplate content={content}>
    <div ref={containerRef} className={`${content?.fullHeightVideo ? 'h-full flex flex-col' : ''}`}>
      {renderSvgComponent()}
      {renderVideo()}
      {renderColumnLayout()}
      {/* Add sections rendering */}
      {renderSections()}
      {renderTools()}
      {renderTips()}
    </div>
  </BaseTemplate>
);
};

// Template for quiz content
// QuizTemplate component with simplified question header inspired by Duolingo/Squla
// QuizTemplate component (replace in TemplateSelector.js)
const QuizTemplate = ({
  content,
  onSelectAnswer,
  selectedAnswer,
  isChecking,
}) => {
  return (
    <BaseTemplate content={content}>
      <div className="quiz-container">
        {/* Progress indicator */}
        {content.currentQuestion && content.totalQuestions && (
          <div className="quiz-progress">
            <div className="quiz-progress-indicator">
              <span className="quiz-progress-text">
                {content.currentQuestion}/{content.totalQuestions}
              </span>
              <div className="quiz-progress-bar">
                <div 
                  className="quiz-progress-fill"
                  style={{ width: `${(content.currentQuestion / content.totalQuestions) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Clean, simple question header */}
        {content?.question && (
          <div className="quiz-header">
            <h2 className="quiz-question">{content.question}</h2>
            {content.subQuestion && (
              <p className="quiz-subquestion">
                {content.subQuestion}
              </p>
            )}
            <div className="quiz-divider"></div>
          </div>
        )}
        
        {/* Answer options with fixed alignment */}
        <div className="quiz-options">
          {content.options?.map((option, index) => {
            // Determine option class based on state
            let optionClass = "quiz-option";
            if (selectedAnswer === index) {
              optionClass += " quiz-option-selected";
              if (isChecking) {
                optionClass += option.correct ? " quiz-option-correct" : " quiz-option-incorrect";
              }
            }
            
            // Determine letter circle class
            let letterClass = "quiz-option-letter";
            if (selectedAnswer === index) {
              letterClass += " quiz-option-letter-selected";
              if (isChecking) {
                letterClass += option.correct ? " quiz-option-letter-correct" : " quiz-option-letter-incorrect";
              }
            }
            
            return (
              <button
                key={index}
                onClick={() => onSelectAnswer && onSelectAnswer(index)}
                className={optionClass}
              >
                <div className="quiz-option-content">
                  {/* Answer letter in a circle */}
                  <div className={letterClass}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  
                  {/* Option content with icon and text */}
                  <div className="quiz-option-inner">
                    <div className="quiz-option-icon">{option.icon}</div>
                    <div className="quiz-option-text">{option.text}</div>
                  </div>
                </div>
                
                {/* Fixed feedback icon positioning */}
                {isChecking && selectedAnswer === index && (
                  <div className="quiz-feedback">
                    <span>
                      {option.correct ? "✅" : "❌"}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </BaseTemplate>
  );
};

// Template for achievement displays
// AchievementTemplate component (replace in TemplateSelector.js)
const AchievementTemplate = ({ content }) => {
  // Try multiple possible locations to find achievements
  // 1. From content.achievements directly
  // 2. From content.content.achievements (nested structure)
  // 3. From parent content if neither is available
  const achievements = 
    content?.achievements || 
    content?.content?.achievements || 
    [];
  
  console.log("AchievementTemplate rendering with:", { 
    contentTitle: content?.title,
    contentType: content?.type,
    hasAchievements: achievements?.length > 0, 
    achievements,
    isCelebration: content?.isCelebration
  });
  
  if (!achievements || !Array.isArray(achievements) || achievements.length === 0) {
    console.warn("AchievementTemplate: No achievements found", content);
    return (
      <BaseTemplate content={content}>
        <div className="achievement-container">
          <p className="template-title">Je hebt deze module voltooid!</p>
        </div>
      </BaseTemplate>
    );
  }

  // The main change is here - we don't return the standard BaseTemplate
  // Instead we use a custom centered layout with the avatar in the middle
  return (
    <div className="achievement-container">
      {/* Add confetti for celebrations */}
      <Confetti count={75} />

      <h2 className="achievement-title">
        {content?.title || "Goed Gedaan! 🎉"}
      </h2>
      
      {/* IMPORTANT: This is the placeholder for where the Avatar should be rendered */}
      {/* The actual Avatar will be rendered by the parent component separately */}
      {/* We're just creating space for it here */}
      <div className="achievement-avatar-space">
        {/* Space for Avatar - it will be centered in this space */}
        <div className="avatar-placeholder"></div>
      </div>

      {/* Achievement badges - simplified as small badge pills */}
      <div className="achievement-badges">
        {achievements.map((achievement, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + (index * 0.15) }}
            className="achievement-badge"
          >
            <span className="achievement-badge-icon">{achievement.icon}</span>
            <span className="achievement-badge-text">{achievement.title}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Template for chart visualization content
const ChartVisualizationTemplate = ({ content, currentLine, ...props }) => {
  // Create a single container ref
  const containerRef = useRef(null);
  
  // Create refs for tools and tips upfront
  const toolRefs = useRef([]);
  const observationRefs = useRef({});
  const tipRefs = useRef([]);
  
  // Initialize refs
  useEffect(() => {
    if (content?.tools) {
      toolRefs.current = Array(content.tools.length).fill().map((_, i) => toolRefs.current[i] || React.createRef());
      
      // Create refs for observations
      const obsRefs = {};
      content.tools.forEach((tool, toolIndex) => {
        if (tool.observations) {
          obsRefs[toolIndex] = Array(tool.observations.length).fill().map((_, i) => 
            observationRefs.current[`${toolIndex}-${i}`] || React.createRef()
          );
        }
      });
      observationRefs.current = obsRefs;
    }
    
    if (content?.tips) {
      tipRefs.current = Array(content.tips.length).fill().map((_, i) => tipRefs.current[i] || React.createRef());
    }
  }, [content]);
  
  // Handle scrolling to highlighted elements
  useEffect(() => {
    if (!content) return;

    // Find the first highlighted tool
    if (content.tools) {
      const highlightedToolIndex = content.tools.findIndex((tool, index) => {
        const isHighlightedBySubtitle = tool.subtitleRange && 
          currentLine >= tool.subtitleRange[0] && 
          currentLine <= tool.subtitleRange[1];
        return isHighlightedBySubtitle || tool.highlight;
      });

      if (highlightedToolIndex !== -1) {
        const tool = content.tools[highlightedToolIndex];
        const ref = toolRefs.current[highlightedToolIndex];
        
        if (ref && ref.current) {
          ref.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest"
          });
          
          // If there's a highlighted item inside this tool, scroll to it
          if (tool.highlightItem !== undefined && 
              observationRefs.current[highlightedToolIndex] &&
              observationRefs.current[highlightedToolIndex][tool.highlightItem] &&
              observationRefs.current[highlightedToolIndex][tool.highlightItem].current) {
            
            setTimeout(() => {
              observationRefs.current[highlightedToolIndex][tool.highlightItem].current.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
              });
            }, 300); // Small delay to scroll to the item after scrolling to the container
          }
          
          return; // Exit early if we already found and scrolled to a highlighted tool
        }
      }
    }

    // If no highlighted tool, check for highlighted tips
    if (content.tips) {
      const highlightedTipIndex = content.tips.findIndex(tip => tip.highlight);
      if (highlightedTipIndex !== -1 && 
          tipRefs.current[highlightedTipIndex] && 
          tipRefs.current[highlightedTipIndex].current) {
        
        tipRefs.current[highlightedTipIndex].current.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    }
  }, [content, currentLine]);

  const renderSvgComponent = () => {
    if (!content?.svgComponent) return null;

    const SvgComp = SVG_COMPONENTS[content.svgComponent];
    if (!SvgComp) {
      console.warn(`SVG component "${content.svgComponent}" not found`);
      return null;
    }

    return (
      <div className="mb-6 bg-[#EFEAFC]/30 p-4 rounded-xl">
        <Suspense
          fallback={
            <div className="h-40 flex items-center justify-center">
              Loading visualization...
            </div>
          }
        >
          <SvgComp />
        </Suspense>
      </div>
    );
  };

 const renderVideo = () => {
    if (!content?.videoUrl) return null

    // For your specific Vimeo URL format
    if (content.videoUrl.includes("vimeo.com")) {
      // Direct embed URL for your specific video
      return (
        <div
          ref={videoContainerRef}
          className={`video-container ${content.fullHeightVideo ? "video-container-fullheight" : ""}`}
        >
          <iframe
            src="https://player.vimeo.com/video/1068648867?h=3f7d5cae54&badge=0&autopause=0&player_id=0&app_id=58479"
            allow="autoplay; fullscreen; picture-in-picture"
            className="video-iframe"
            title={content.videoTitle || "Embedded video"}
            allowFullScreen={true}
            loading="lazy"
          />
          <button onClick={toggleFullscreen} className="video-fullscreen-button">
            {isFullscreen ? <Minimize2 className="w-5 h-5 text-white" /> : <Maximize2 className="w-5 h-5 text-white" />}
          </button>
        </div>
      )
    }

    let srcUrl = content.videoUrl
    const isYouTube = content.videoUrl.includes("youtube.com") || content.videoUrl.includes("youtu.be")
    const isVimeo = content.videoUrl.includes("vimeo.com")

    if (isYouTube) {
      // Extract video ID from YouTube URL
      let videoId = content.videoUrl.match(/v=([^&]+)/)?.[1]
      if (!videoId) {
        videoId = content.videoUrl.match(/youtu.be\/([^&]+)/)?.[1]
      }
      if (videoId) {
        srcUrl = `https://www.youtube.com/embed/${videoId}`
      }
    }

    // For Vimeo, extract ID and format URL
    if (isVimeo) {
      // Extract the video ID from various Vimeo URL formats
      let videoId

      // Handle URLs with /videos/ format
      if (content.videoUrl.includes("/videos/")) {
        videoId = content.videoUrl.match(/(?:vimeo\.com\/videos\/)(\d+)/)?.[1]
      }
      // Handle URLs with share parameter format (like your URL)
      else if (content.videoUrl.includes("share=copy")) {
        videoId = content.videoUrl.match(/(?:vimeo\.com\/)(\d+)/)?.[1]
      }
      // Handle standard Vimeo URLs
      else {
        videoId = content.videoUrl.match(/(?:vimeo\.com\/)(\d+)/)?.[1]
      }

      if (videoId) {
        srcUrl = `https://player.vimeo.com/video/${videoId}?h=ef9c24aa47&amp;badge=0&amp;autopause=0&player_id=0&app_id=58479`
        console.log("Vimeo embed URL:", srcUrl) // Add this for debugging
      } else {
        console.error("Could not extract Vimeo video ID from:", content.videoUrl)
      }
    }

    return (
      <div className="video-container">
        <iframe title="Video" src={srcUrl} allowFullScreen></iframe>
      </div>
    )
  }

  const renderChartComponent = () => {
    // Special handling for our step-by-step chart visualization
    if (content?.chartStepData) {
      return <ChartVisualization stepData={content.chartStepData} />;
    }

    // Check what kind of chart visualization to show
    if (content?.visualizationType === "interactive") {
      return <BarChartExamples />;
    } else if (content?.chartData) {
      // Simple chart with provided data
      return (
        <InteractiveBarChart
          initialData={content.chartData}
          title={content.chartTitle || "Data Chart"}
          interactive={content.interactive !== false}
          height={content.chartHeight || 220} // Reduced height
          colorScheme={content.colorScheme}
          animateChanges={content.animateChanges !== false}
        />
      );
    }

    // Default to examples if no specific type is provided
    return <BarChartExamples />;
  };
  
  // This function should be part of the ContentTemplate component in TemplateSelector.js
const renderTools = () => {
  if (!content?.tools) return null;

  return (
    <div className="tools-grid">
      {content.tools.map((tool, index) => {
        // Determine if this tool should be highlighted
        const isHighlightedBySubtitle = tool.subtitleRange && 
          currentLine >= tool.subtitleRange[0] && 
          currentLine <= tool.subtitleRange[1];
        
        const shouldHighlight = isHighlightedBySubtitle || tool.highlight;
        
        // Determine the theme class
        let themeClass = "tool-card";
        if (tool.theme === "blue") {
          themeClass += " tool-card-blue";
        } else if (tool.theme === "purple") {
          themeClass += " tool-card-purple";
        } else {
          themeClass += " tool-card-green";
        }
        
        if (shouldHighlight) {
          themeClass += " tool-card-highlighted";
        }

        return (
          <motion.div
            key={index}
            ref={el => toolRefs.current[index] = { current: el }}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: shouldHighlight ? 1.05 : 1,
            }}
            transition={{
              delay: index * 0.2,
              duration: shouldHighlight ? 0.3 : 0.2,
            }}
            className={themeClass}
            data-highlighted={shouldHighlight ? "true" : "false"}
          >
            <div className="tool-content">
              <motion.div
                animate={{
                  y: [0, -5, 0],
                  rotate: [0, 5, -5, 0],
                  scale: shouldHighlight ? [1, 1.1, 1] : [1, 1, 1],
                }}
                transition={{
                  duration: shouldHighlight ? 2 : 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="tool-icon-container"
              >
                {tool.image ? (
                  <ClickableImage 
                    src={tool.image || "/placeholder.svg"} 
                    alt={tool.title || "Icon"} 
                    className="tool-icon-image"
                  />
                ) : (
                  <div className="tool-icon-emoji">{tool.icon}</div>
                )}
              </motion.div>
              <h3 className="tool-title">
                {tool.title}
              </h3>
              <p className="tool-description">{tool.description}</p>
              {tool.examples && (
                <div className="tool-examples">
                  {tool.examples.map((example, i) => (
                    <span key={i} className="tool-example">
                      {example}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {tool.observations && (
              <div className="tool-observations">
                <p className="tool-subtitle">
                  {tool.subTitle}
                </p>
                <ul className="tool-observation-list">
                  {tool.observations.map((observation, i) => {
                    const isHighlighted = tool.highlightItem === i;
                    return (
                      <motion.li
                        key={i}
                        ref={el => {
                          if (!observationRefs.current[index]) {
                            observationRefs.current[index] = [];
                          }
                          observationRefs.current[index][i] = { current: el };
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: isHighlighted ? 1.05 : 1,
                        }}
                        transition={{
                          delay: i * 0.1,
                          duration: isHighlighted ? 0.3 : 0.2,
                        }}
                        className={`observation-item ${isHighlighted ? 'observation-item-highlighted' : ''}`}
                        data-highlighted={isHighlighted ? "true" : "false"}
                      >
                        <span className={`observation-marker ${isHighlighted ? 'observation-marker-highlighted' : ''}`}>
                          {isHighlighted ? "→" : "•"}
                        </span>
                        <span>{observation}</span>
                      </motion.li>
                    );
                  })}
                </ul>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};
  

  // This function should be part of the ContentTemplate component in TemplateSelector.js
const renderTips = () => {
  if (!content?.tips) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="tips-container"
    >
      <ul className="tips-list">
        {content.tips.map((tip, index) => (
          <motion.li
            key={index}
            ref={el => tipRefs.current[index] = { current: el }}
            initial={{ opacity: 0, x: -20 }}
            animate={{
              opacity: 1,
              x: 0,
              scale: tip.highlight ? 1.05 : 1,
            }}
            transition={{
              delay: index * 0.1,
              duration: tip.highlight ? 0.3 : 0.2,
            }}
            className={`tip-item ${tip.highlight ? 'tip-item-highlighted' : ''}`}
            data-highlighted={tip.highlight ? "true" : "false"}
          >
            <motion.span
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, tip.highlight ? 1.3 : 1.2, 1],
              }}
              transition={{
                duration: tip.highlight ? 1 : 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: index * 0.2,
              }}
              className="tip-icon"
            >
              {tip.iconImage ? (
                <ClickableImage 
                  src={tip.iconImage || "/placeholder.svg"} 
                  alt="Tip icon"
                  className="tip-icon-image" 
                />
              ) : (
                tip.icon
              )}
            </motion.span>
            <span className={tip.highlight ? "tip-text tip-text-highlighted" : "tip-text"}>
              {tip.text}
            </span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
};
  return (
    <BaseTemplate content={content}>
      <div className="space-y-8" ref={containerRef}>
        {/* SVG Visualization */}
        {renderSvgComponent()}

        {/* Optional video section from base template */}
        {content?.videoUrl && renderVideo()}

        {/* Chart visualization component */}
        {renderChartComponent()}

        {/* Render any additional tools or tips */}
        {renderTools()}
        {renderTips()}
      </div>
    </BaseTemplate>
  );
};

// Export individual templates for direct use if needed
export {
  ContentTemplate,
  QuizTemplate,
  AchievementTemplate,
  ChartVisualizationTemplate,
  ChartStepTemplate,
};

// Main template selector component
const TemplateSelector = ({ type, content, currentLine, ...props }) => {
  if (type === "achievements" || content?.achievements || content?.type === "celebration") {
    console.log("TemplateSelector received:", {
      type,
      contentType: content?.type,
      hasAchievements: content?.achievements?.length > 0,
      achievements: content?.achievements,
      isCelebration: content?.isCelebration
    });
  }
  switch (type) {
    case "quiz":
      return <QuizTemplate content={content} {...props} />;
    case "achievements":
      return <AchievementTemplate content={content} {...props} />;
    case "chartVisualization":
      return <ChartVisualizationTemplate content={content} currentLine={currentLine} {...props} />;
    case "chartStep":
      return <ChartStepTemplate content={content} currentLine={currentLine} {...props} />;
    case "content":
      return <ContentTemplate content={content} currentLine={currentLine} {...props} />;
    default:
      console.warn(
        `Unknown template type "${type}", falling back to ContentTemplate`
      );
      return <ContentTemplate content={content} currentLine={currentLine} {...props} />;
  }
};

export default TemplateSelector;
